import React from 'react'
import { useFormikContext } from 'formik'
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Box,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt'
import ApartmentIcon from '@mui/icons-material/Apartment'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import FoundationIcon from '@mui/icons-material/Foundation'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import NoteIcon from '@mui/icons-material/Note'
import ColorButtonGroup from '../components/ColorButtonGroup'
import {
  GENERAL_RISK_FIELD_FALLBACK,
  resolveGeneralRiskFieldName,
} from '../../../utils/riskFieldHelpers'

const SectionHeader = ({ icon: Icon, label }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
    <Typography variant="subtitle2" color="primary" fontWeight={600}>
      {label}
    </Typography>
  </Stack>
)

// Colores para niveles de riesgo general (4 niveles incluyendo crítico)
const GENERAL_RISK_COLOR_ORDER = {
  '#4caf50': 0,
  '#ff9800': 1,
  '#f44336': 2,
  '#b71c1c': 3,
  '#9e9e9e': 4,
}

const getGeneralRiskColor = label => {
  const lower = (label || '').toLowerCase()
  if (lower.includes('baj')) return '#4caf50'
  if (lower.includes('med')) return '#ff9800'
  if (lower.includes('alt')) return '#f44336'
  if (lower.includes('crít') || lower.includes('crit')) return '#b71c1c'
  return '#9e9e9e'
}

const buildGeneralRiskOptions = item => {
  if (!item?.options || typeof item.options !== 'object') return []
  const parsed = Object.entries(item.options)
    .map(([value, label]) => {
      const color = getGeneralRiskColor(label)
      if (color === '#9e9e9e') console.warn(`[RiskStep] Label sin color reconocido: "${label}"`)
      return { value, label, color }
    })
    .sort(
      (a, b) =>
        (GENERAL_RISK_COLOR_ORDER[a.color] ?? 9) -
        (GENERAL_RISK_COLOR_ORDER[b.color] ?? 9),
    )
  return parsed
}

const PRIORITY_OPTIONS = [
  { value: 'baja', label: 'Baja', color: '#4caf50' },
  { value: 'media', label: 'Media', color: '#ff9800' },
  { value: 'alta', label: 'Alta', color: '#f44336' },
  { value: 'urgente', label: 'Urgente', color: '#b71c1c' },
]

const RISK_SWITCH_CONFIG = [
  {
    name: 'rsgoCaida',
    title: 'Riesgo de caída',
    description: 'El árbol presenta riesgo de caerse',
    icon: ReportProblemIcon,
    color: '#ff9800',
  },
  {
    name: 'rsgoRedesElectricas',
    title: 'Interferencia con cables',
    description: 'Contacto con redes eléctricas',
    icon: ElectricBoltIcon,
    color: '#ffc107',
  },
  {
    name: 'rsgoAfeccionConstr',
    title: 'Afección a construcción',
    description: 'Daño a estructuras cercanas',
    icon: ApartmentIcon,
    color: '#2196f3',
  },
  {
    name: 'rsgoInterfCirculacion',
    title: 'Interferencia circulación',
    description: 'Obstruye vías o aceras',
    icon: DirectionsCarIcon,
    color: '#9c27b0',
  },
  {
    name: 'rsgoInfraestructura',
    title: 'Riesgo a infraestructura',
    description: 'Afecta tuberías, cableado subterráneo',
    icon: FoundationIcon,
    color: '#607d8b',
  },
]
const ORDERED_RISK_FIELDS = RISK_SWITCH_CONFIG.map(item => item.name)

const RISK_LEVEL_ORDER = ['bajo', 'medio', 'alto']
const RISK_LEVEL_COLORS = {
  bajo: '#4caf50',
  medio: '#ff9800',
  alto: '#f44336',
}

const DEFAULT_RISK_LEVELS = [
  { value: 'Bajo', label: 'Bajo', color: RISK_LEVEL_COLORS.bajo },
  { value: 'Medio', label: 'Medio', color: RISK_LEVEL_COLORS.medio },
  { value: 'Alto', label: 'Alto', color: RISK_LEVEL_COLORS.alto },
]

const getRiskLevelKey = label => {
  const lower = (label || '').toLowerCase()
  if (lower.includes('baj')) return 'bajo'
  if (lower.includes('med')) return 'medio'
  if (lower.includes('alt')) return 'alto'
  return null
}

const buildRiskLevelOptions = item => {
  if (!item?.options || typeof item.options !== 'object') return []
  const parsed = Object.entries(item.options)
    .map(([value, label]) => {
      const key = getRiskLevelKey(label)
      if (!key) {
        console.warn(`[RiskStep] Label de riesgo sin nivel reconocido: "${label}"`)
        return null
      }
      return {
        value,
        label,
        color: RISK_LEVEL_COLORS[key],
        order: RISK_LEVEL_ORDER.indexOf(key),
      }
    })
    .filter(Boolean)

  if (!parsed.length) return []

  const seen = new Set()
  return parsed
    .sort((a, b) => a.order - b.order)
    .filter(option => {
      const key = getRiskLevelKey(option.label)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .map(({ value, label, color }) => ({ value, label, color }))
}

const RiskLevelCard = React.forwardRef(
  ({ icon: Icon, title, description, options, selected, onChange, color }, ref) => (
    <Box
      ref={ref}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.50' : 'background.paper',
        transition: 'all 0.2s',
      }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        {Icon && <Icon sx={{ fontSize: 22, color }} />}
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {title}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" spacing={1.5}>
        {options.map(option => {
          const isSelected = selected === option.value
          return (
            <Box
              key={option.value}
              onClick={() => onChange(option.value)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                minHeight: 46,
                px: 1.5,
                borderRadius: 2,
                border: isSelected ? '2px solid' : '1.5px solid',
                borderColor: isSelected ? option.color : 'divider',
                bgcolor: isSelected ? `${option.color}20` : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.15s',
                userSelect: 'none',
                '&:hover': { borderColor: option.color, bgcolor: `${option.color}0A` },
                '&:active': { transform: 'scale(0.97)' },
              }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: option.color,
                }}
              />
              <Typography
                variant="body2"
                fontWeight={isSelected ? 700 : 500}
                sx={{
                  color: isSelected ? option.color : 'text.primary',
                  transition: 'color 0.15s',
                }}>
                {option.label}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Box>
  ),
)

RiskLevelCard.displayName = 'RiskLevelCard'

const RiskStep = ({ itemsInconvenience, generalRiskFieldName }) => {
  const { values, setFieldValue } = useFormikContext()
  const fieldRefs = React.useRef({})
  const otherRiskInputRef = React.useRef(null)
  const [showOtherRiskField, setShowOtherRiskField] = React.useState(
    Boolean(values?.riesgoOtros?.toString().trim()),
  )

  // Build a map of risk items from inconvenience data
  const riskItemsMap = React.useMemo(() => {
    const map = {}
    if (!itemsInconvenience) return map
    itemsInconvenience
      .filter(item => item.category === 7)
      .forEach(item => {
        map[item.name] = item
      })
    return map
  }, [itemsInconvenience])

  const resolvedGeneralRiskFieldName = React.useMemo(
    () =>
      resolveGeneralRiskFieldName(
        itemsInconvenience,
        generalRiskFieldName || GENERAL_RISK_FIELD_FALLBACK,
      ),
    [itemsInconvenience, generalRiskFieldName],
  )

  React.useEffect(() => {
    if (values?.riesgoOtros?.toString().trim()) {
      setShowOtherRiskField(true)
    }
  }, [values?.riesgoOtros])

  const handleRiskChange = React.useCallback(
    (fieldName, value) => {
      setFieldValue(fieldName, value)
      const currentIdx = ORDERED_RISK_FIELDS.indexOf(fieldName)
      if (currentIdx < ORDERED_RISK_FIELDS.length - 1) {
        const nextFieldName = ORDERED_RISK_FIELDS[currentIdx + 1]
        setTimeout(() => {
          fieldRefs.current[nextFieldName]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }, 150)
      }
    },
    [setFieldValue],
  )

  const handleToggleOtherRisk = event => {
    const checked = event.target.checked
    setShowOtherRiskField(checked)
    if (!checked) {
      setFieldValue('riesgoOtros', '')
    } else {
      setTimeout(() => {
        otherRiskInputRef.current?.focus()
        otherRiskInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 150)
    }
  }

  const generalRiskOptions = buildGeneralRiskOptions(
    riskItemsMap[resolvedGeneralRiskFieldName],
  )
  const hasGeneralRiskOptions = generalRiskOptions.length > 0

  React.useEffect(() => {
    if (hasGeneralRiskOptions || process.env.NODE_ENV === 'production') return
    const riskKeys = Object.keys(riskItemsMap)
    console.warn(
      '[RiskStep] Nivel de Riesgo General sin opciones válidas del backend.',
      { resolvedGeneralRiskFieldName, riskKeys },
    )
  }, [hasGeneralRiskOptions, resolvedGeneralRiskFieldName, riskItemsMap])

  return (
    <Stack spacing={2.5}>
      {/* Risk Switches */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={ReportProblemIcon} label="Evaluación de Riesgos" />
          <Stack spacing={1.5}>
            {RISK_SWITCH_CONFIG.map(({ name, title, description, icon, color }) => {
              const item = riskItemsMap[name]
              const options = buildRiskLevelOptions(item)
              return (
                <RiskLevelCard
                  key={name}
                  ref={el => {
                    fieldRefs.current[name] = el
                  }}
                  icon={icon}
                  title={title}
                  description={description}
                  options={options}
                  selected={values[name] || ''}
                  onChange={val => handleRiskChange(name, val)}
                  color={color}
                />
              )
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Other Risks */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={NoteIcon} label="Otros Riesgos" />
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={showOtherRiskField}
                  onChange={handleToggleOtherRisk}
                  size="small"
                />
              }
              label="Agregar otros riesgos"
            />
            {showOtherRiskField && (
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                size="small"
                name="riesgoOtros"
                inputRef={otherRiskInputRef}
                placeholder="Describa otros riesgos observados..."
                value={values.riesgoOtros || ''}
                onChange={e => setFieldValue('riesgoOtros', e.target.value)}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* General Risk Level */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={WarningIcon} label="Nivel de Riesgo General" />
          {hasGeneralRiskOptions ? (
            <ColorButtonGroup
              options={generalRiskOptions}
              selected={values[resolvedGeneralRiskFieldName] || ''}
              onChange={val => setFieldValue(resolvedGeneralRiskFieldName, val)}
              columns={4}
            />
          ) : (
            <Typography variant="body2" color="error">
              No se pudieron cargar los niveles de riesgo general configurados.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Attention Priority (NEW) */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader
            icon={PriorityHighIcon}
            label="Urgencia de intervención del árbol"
          />
          <ColorButtonGroup
            options={PRIORITY_OPTIONS}
            selected={values.prioridadAtencion || ''}
            onChange={val => setFieldValue('prioridadAtencion', val)}
            columns={4}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}>
            Define la prioridad de atención para este árbol
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  )
}

export default RiskStep
